/**
 * KnowledgeSearch — RAG-based query input and answer display.
 */

import { useState } from 'react';
import { useAuth } from '../../../components/AuthProvider';
import { useToast } from '../../../components/ToastProvider';
import { submitQuery } from '../../../api/query';
import type { QueryResponse } from '../../../api/types';

export function KnowledgeSearch() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [queryText, setQueryText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!queryText.trim() || !user) return;

    setLoading(true);
    setResult(null);
    setSearchError(null);

    try {
      const response = await submitQuery({
        tenant_id: user.tenantId,
        user_role: user.role,
        query_text: queryText,
      });
      setResult(response);
    } catch {
      setSearchError('Query failed. The backend may be unavailable or no documents have been indexed yet.');
      showToast('Query failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Knowledge Search</h1>
        <p className="text-body text-slate-muted mt-1">
          Query your CTI knowledge base using natural language
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className="glass-card">
        <label htmlFor="query-input" className="block text-body font-semibold text-slate-text mb-3">
          Ask a question about your threat intelligence
        </label>
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            id="query-input"
            type="text"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="e.g., What TTPs does APT29 use for initial access?"
            className="flex-1 px-4 py-3 text-body rounded-btn border-2 focus:outline-none focus:border-primary-blue bg-white"
            style={{ borderColor: 'var(--color-card-border)' }}
            disabled={loading}
          />
          <button type="submit" className="btn-primary whitespace-nowrap" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {/* Error state */}
      {searchError && !result && (
        <div className="glass-card text-center py-8">
          <p className="text-body text-red-700">{searchError}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Answer */}
          <div className="glass-card">
            <h3 className="text-section-sm font-bold text-slate-text mb-3">Answer</h3>
            <p className="text-body text-slate-text whitespace-pre-wrap">{result.answer}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-card">
              <p className="text-[16px] font-semibold text-slate-muted mb-1">Confidence</p>
              <p className="text-section font-bold text-primary-teal">
                {(result.confidence * 100).toFixed(0)}%
              </p>
            </div>
            <div className="glass-card">
              <p className="text-[16px] font-semibold text-slate-muted mb-1">Policy Applied</p>
              <p className="text-section font-bold text-primary-blue">{result.policy_applied}</p>
            </div>
            <div className="glass-card">
              <p className="text-[16px] font-semibold text-slate-muted mb-1">Sources</p>
              <p className="text-section font-bold text-primary-green">{result.sources.length}</p>
            </div>
          </div>

          {/* Sources list */}
          {result.sources.length > 0 && (
            <div className="glass-card">
              <h3 className="text-section-sm font-bold text-slate-text mb-3">Sources</h3>
              <ul className="space-y-2">
                {result.sources.map((source, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-body text-slate-text">
                    <span className="w-6 h-6 flex items-center justify-center bg-primary-teal/10 rounded text-[14px] font-bold text-primary-teal">
                      {idx + 1}
                    </span>
                    {source}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
